import { z } from 'zod'

import { PaymentProvider } from '../constants/pricing'
import { StripeConnectType } from '../constants/venue'
import { dbEntitySchema } from './system'

export const paymentProviderCustomerOwnerSchema = z.object({
  user: z.string().optional().nullable(),
  prospect: z.string().optional().nullable(),
  organization: z.string().optional().nullable(),
})

export const paymentProviderCustomerSchema = dbEntitySchema.extend({
  ...paymentProviderCustomerOwnerSchema.shape,
  providerType: z.nativeEnum(PaymentProvider),
  providerCustomerId: z.string(),
  providerAccountId: z.string().optional().nullable(),
  stripeConnectType: z.nativeEnum(StripeConnectType).optional().nullable(),
})

export const paymentProviderClientConfigSchema = z.object({
  clientToken: z.string(),
  appleMerchantIdentifier: z.string().nullable().optional(),
})

export const paymentProviderClientConfigResultSchema =
  paymentProviderClientConfigSchema.extend({
    type: z.nativeEnum(PaymentProvider),
  })

export const paymentProviderCreatePaymentMethodArgsSchema = z
  .object({
    userId: z.string().optional(),
    prospectId: z.string().optional(),
    organizationId: z.string().optional(),
    providerToken: z.string(),
    providerAccountId: z.string().optional(),
    stripeConnectType: z.nativeEnum(StripeConnectType).optional(),
  })
  .refine(
    (data) => Boolean(data.userId || data.prospectId || data.organizationId),
    {
      message: 'userId, prospectId, or organizationId is required',
    }
  )

export const paymentProviderPaymentMethodCreateArgsSchema = z.object({
  customerId: z.string(),
  providerToken: z.string(),
})

export const paymentProviderPaymentMethodSchema = z.object({
  providerPaymentId: z.string(),
  providerCustomerId: z.string(),
  last4: z.string().optional().nullable(),
  expirationMonth: z.union([z.string(), z.number()]).optional().nullable(),
  expirationYear: z.union([z.string(), z.number()]).optional().nullable(),
  paymentMethodType: z.string().optional().nullable(),
  walletType: z.string().optional().nullable(),
  cardFunding: z.string().optional().nullable(),
  cardCountry: z.string().optional().nullable(),
  cardType: z.string().optional().nullable(),
  zip: z.string().optional().nullable(),
})

export const paymentProviderChargeCreateArgsSchema = z.object({
  amount: z.number(),
  providerPaymentId: z.string(),
  providerCustomerId: z.string(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  descriptor: z.string().optional(),
  providerAccountId: z.string().optional(),
  stripeConnectType: z.nativeEnum(StripeConnectType).optional(),
  currencyCode: z.string().optional(),
  providerChargeId: z.string().optional(),
  applicationFeeAmount: z.number().optional(),
})

export const paymentProviderChargeSchema = z.object({
  providerChargeId: z.string().nullable(),
  settled: z.boolean(),
  amount: z.number(),
  currencyCode: z.string().optional(),
  settlementCurrencyCode: z.string().nullable().optional(),
  exchangeRate: z.number().nullable().optional(),
  amountUsd: z.number().nullable().optional(),
  feeUsd: z.number().nullable().optional(),
  applicationFeeUsd: z.number().nullable().optional(),
  refunded: z.boolean().optional(),
  fee: z.number().nullable().optional(),
  applicationFee: z.number().nullable().optional(),
})

export const paymentProviderRefundChargeArgsSchema = z.object({
  amount: z.number().optional(),
  providerAccountId: z.string().optional(),
  stripeConnectType: z.nativeEnum(StripeConnectType).optional(),
})

export const paymentProviderRefundSchema = z.object({
  amount: z.number().nullable(),
  providerRefundId: z.string().nullable().optional(),
})

export const paymentProviderChargeUpdateArgsSchema = z.object({
  metadata: z.record(z.string(), z.unknown()).optional(),
  providerAccountId: z.string().optional(),
  stripeConnectType: z.nativeEnum(StripeConnectType).optional(),
})

export const paymentProviderCustomerCreateDataSchema = z.object({
  resourceId: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  userId: z.string().optional(),
  prospectId: z.string().optional(),
  organizationId: z.string().optional(),
})

export const paymentProviderCustomerResultSchema = z.object({
  providerCustomerId: z.string(),
})
